import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Pause, Play, RotateCcw, SkipForward } from 'lucide-react'
import { api } from '@/runtime'
import type { FocusSession } from '@/db/types'
import { useResource } from '@/db/hooks'
import type { TaskResource } from '@/kinds/task/apply'
import type { TaskSpec, TaskStatus } from '@/kinds/task/schema'
import { useUi } from '@/state/ui'
import { PHASE_LABEL, askNotify, usePomodoro } from '@/state/pomodoro'
import { Button, Field, PageTitle, SectionTitle, Segmented } from '@/components/ui'
import { PomodoroDial, mmss, usePomodoroClock } from '@/components/Pomodoro'
import { Phrases } from '@/components/Phrases'

/**
 * Focus: the pomodoro in full. The dial, the controls, the lengths, and the
 * sessions it has recorded (in the library, where Insights reads them). It
 * can run on the active task, so the time lands against something.
 */
export const FocusView = () => {
  const p = usePomodoro()
  const left = usePomodoroClock()
  const activeId = useUi((s) => s.activeTaskId)
  const active = useResource<TaskSpec, TaskStatus>(activeId ?? undefined) as TaskResource | undefined
  const [sessions, setSessions] = useState<FocusSession[] | null>(null)
  useEffect(() => {
    api
      .focusSessions(7)
      .then((r) => setSessions(r.sessions))
      .catch(() => setSessions([]))
  }, [p.status, p.round])
  const startOnTask = () => {
    askNotify()
    p.start(active && !active.deleted_at ? { id: active.id, title: active.spec.title } : null)
  }
  return (
    <div data-testid="focus">
      <PageTitle eyebrow="Pomodoro">Focus</PageTitle>
      <div className="card p-5 flex flex-col md:flex-row items-center gap-6 mb-5">
        <PomodoroDial size={260} left={left} />
        <div className="flex flex-col gap-3 items-center md:items-start min-w-0">
          <Phrases
            as="div"
            className="text-lg font-medium"
            parts={[PHASE_LABEL[p.phase], `round ${p.round} of ${p.config.rounds}`]}
          />
          {p.title ? (
            <div className="text-sm text-muted truncate max-w-full">On: {p.title}</div>
          ) : active && !active.deleted_at ? (
            <div className="text-sm text-muted truncate max-w-full">Will run on: {active.spec.title}</div>
          ) : (
            <div className="text-sm text-faint">
              Start a task first to log the time against it, or just focus.
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {p.status === 'running' ? (
              <Button big onClick={p.pause} data-testid="focus-pause">
                <Pause size={18} /> Pause
              </Button>
            ) : (
              <Button big variant="primary" onClick={startOnTask} data-testid="focus-start">
                <Play size={18} /> {p.status === 'paused' ? 'Resume' : 'Start'}
              </Button>
            )}
            <Button big onClick={p.skip} disabled={p.status === 'idle'} data-testid="focus-skip">
              <SkipForward size={18} /> Skip
            </Button>
            <Button
              big
              variant="quiet"
              onClick={p.reset}
              disabled={p.status === 'idle' && p.round === 1 && p.phase === 'work'}
              data-testid="focus-reset"
            >
              <RotateCcw size={18} /> Reset
            </Button>
          </div>
          {p.lastEnded && (
            <div className="text-sm text-emerald-300/90" role="status">
              {p.lastEnded.phase === 'work' ? 'Focus done. Break time.' : 'Break over. Back to it.'}
            </div>
          )}
        </div>
      </div>

      <SectionTitle>Lengths</SectionTitle>
      {/* each picker as wide as its choices, wrapping as a whole: two in a fixed grid cell overlapped */}
      <div className="card p-3 mb-5 flex flex-wrap gap-x-6 gap-y-1">
        <Field label="Focus">
          <Segmented
            label="Focus minutes"
            value={p.config.work}
            options={[15, 20, 25, 30, 45, 50].map((m) => ({ value: m, label: `${m}` }))}
            onChange={(v) => p.setConfig({ work: v })}
          />
        </Field>
        <Field label="Short break">
          <Segmented
            label="Short break minutes"
            value={p.config.short}
            options={[3, 5, 10].map((m) => ({ value: m, label: `${m}` }))}
            onChange={(v) => p.setConfig({ short: v })}
          />
        </Field>
        <Field label="Long break">
          <Segmented
            label="Long break minutes"
            value={p.config.long}
            options={[10, 15, 20, 30].map((m) => ({ value: m, label: `${m}` }))}
            onChange={(v) => p.setConfig({ long: v })}
          />
        </Field>
        <Field label="Rounds before a long break">
          <Segmented
            label="Rounds"
            value={p.config.rounds}
            options={[2, 3, 4, 5, 6].map((m) => ({ value: m, label: `${m}` }))}
            onChange={(v) => p.setConfig({ rounds: v })}
          />
        </Field>
        <Field label="After a phase">
          <Segmented
            label="Auto start"
            value={p.config.autoNext ? 'auto' : 'wait'}
            options={[
              { value: 'auto', label: 'Start the next' },
              { value: 'wait', label: 'Wait for me' },
            ]}
            onChange={(v) => p.setConfig({ autoNext: v === 'auto' })}
          />
        </Field>
        <Field label="Chime">
          <Segmented
            label="Chime"
            value={p.config.sound ? 'on' : 'off'}
            options={[
              { value: 'on', label: 'On' },
              { value: 'off', label: 'Off' },
            ]}
            onChange={(v) => p.setConfig({ sound: v === 'on' })}
          />
        </Field>
        <Field label="Notification" hint="The browser's own, when it allows it.">
          <Segmented
            label="Notification"
            value={p.config.notify ? 'on' : 'off'}
            options={[
              { value: 'on', label: 'On' },
              { value: 'off', label: 'Off' },
            ]}
            onChange={(v) => {
              if (v === 'on') askNotify()
              p.setConfig({ notify: v === 'on' })
            }}
          />
        </Field>
      </div>

      <SectionTitle
        right={
          <Link
            to="/music/insights"
            search={{ metric: 'focused', days: 30 } as never}
            className="text-xs underline text-muted"
          >
            in Insights
          </Link>
        }
      >
        This week
      </SectionTitle>
      <div className="card divide-y divide-line" data-testid="focus-sessions">
        {(sessions ?? []).slice(0, 12).map((s) => (
          <div key={s.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate">
              {s.data.title || 'Focus'}
              {!s.data.completed && <span className="text-faint ml-3">stopped early</span>}
            </span>
            <Phrases
              className="text-xs text-faint tabular-nums shrink-0"
              parts={[
                mmss((s.value ?? 0) * 60_000),
                new Date(s.at).toLocaleString(undefined, {
                  weekday: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              ]}
            />
          </div>
        ))}
        {sessions && sessions.length === 0 && (
          <div className="px-3 py-2 text-sm text-faint">
            Nothing yet. The first finished round lands here.
          </div>
        )}
      </div>
    </div>
  )
}
